import {
  ChoosingRecipe,
  LegacySurvey,
  MealPlanQuestion,
  Params,
  Payload,
  QuizAnswer,
} from "../typings";
import { MealLabels } from "../typings/enums";
import { API_Root } from "./FuturHealthLegacyApiRoot";
const { VITE_LEGACY_SERVER_URL } = import.meta.env;
const SERVER_PRODUCTION = VITE_LEGACY_SERVER_URL || "https://fh.co";

export default class FuturHealthLegacyAPI {
  private static domain: string = SERVER_PRODUCTION;

  public static getUserToken(data: Partial<Payload>, firebaseToken: string) {
    return API_Root<IToken, Partial<Payload>>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/nutrition-plans/firebase-auth-user-token/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Bearer ${firebaseToken}`,
      },
      data,
    });
  }

  public static checkAuth(token: string | null) {
    return API_Root<IAuth, unknown>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/auth/check/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  public static createMisc(token: string, data: Partial<Payload>) {
    return API_Root({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/misc/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      data,
    });
  }
  public static getMisc(token: string, key: string) {
    return API_Root<{ value: string }, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/misc/${key}/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
    });
  }
  public static putMisc(token: string, data: Partial<Payload>) {
    return API_Root({
      method: "put",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/misc/${data.key}/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      data,
    });
  }

  public static setMisc(token: string, data: Partial<Payload>) {
    return FuturHealthLegacyAPI.putMisc(token, data)
      .then(({ data }) => data)
      .catch(() => {
        return FuturHealthLegacyAPI.createMisc(token, data)
          .then(({ data }) => data)
          .catch((err) => {
            throw err;
          });
      });
  }

  public static createQuiz(quizId: number = 134, token?: string) {
    return API_Root<{ id: number }, { survey: number }>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/usersurveys/`,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
      data: {
        survey: quizId,
      },
    });
  }

  public static getMetabolicType() {
    return API_Root<{ metabolic_type: number }, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/usersurveys/134/`,
    });
  }

  public static getMealPlanQuiz() {
    return API_Root<{ questions: MealPlanQuestion[] }, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/surveys/134/`,
    });
  }

  public static getLegacySurveys(token?: string) {
    return API_Root<LegacySurvey[], unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/surveys/`,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  static deleteQuizAnswer(quizId: number, surveyId: number) {
    return API_Root({
      method: "delete",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/usersurveys/${quizId}/surveyanswers/${surveyId}/`,
    });
  }

  public static postQuizAnswer(
    quizID: number,
    questionId: number,
    answerIds: number[],
    isSingle: true
  ): Promise<import("axios").AxiosResponse<QuizAnswer>>;

  public static postQuizAnswer(
    quizID: number,
    questionId: number,
    answerIds: number[],
    isSingle: false
  ): Promise<import("axios").AxiosResponse<QuizAnswer[]>>;

  public static postQuizAnswer(
    quizID: number,
    questionId: number,
    answerIds: number[],
    isSingle: boolean
  ) {
    const body: QuizAnswer | QuizAnswer[] = isSingle
      ? {
          question: questionId,
          answer_choice: answerIds[0],
        }
      : answerIds.map((it: number) => ({
          question: questionId,
          answer_choice: it,
        }));

    return API_Root<QuizAnswer | QuizAnswer[], unknown>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/usersurveys/${quizID}/surveyanswers/`,
      data: body,
    });
  }

  public static sendTypedAnswer(answer: {
    user_survey_answer: number;
    answer: string;
  }) {
    return API_Root({
      method: "post",
      url: `${import.meta.env.VITE_LEGACY_SERVER_URL}/api/v1/usersurveys/typed-answer/`,
      data: JSON.stringify(answer),
    });
  }

  public static getWeekInfo(token: string | null) {
    return API_Root<IWeekInfo, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/checkins/week-navigator/`,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  static getAllRecipes(token: string | null) {
    return API_Root<IRecipeInLibrary[], undefined>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/recipes/?restrictions=1`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  static getRecipeById({
    recipeId,
    token,
  }: {
    recipeId: string;
    token: string | null;
  }) {
    return API_Root<IRecipeInLibrary, undefined>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/recipes/${recipeId}/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  static getRecipeFilters(token: string | null) {
    return API_Root<IRecipeFilter[], undefined>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/tags-categories/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  public static getMealPlan({
    data,
    token,
  }: {
    token: string;
    data?: {
      weekNumber?: number;
      dayNumber?: number;
      checkin?: number;
    };
  }) {
    return API_Root<IMealPlan, IGetMealPlansParams>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/new-mymealplan/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Token ${token}`,
      },
      params: {
        week_number: data?.weekNumber,
        day_number: data?.dayNumber,
        checkin: data?.checkin,
      },
    });
  }

  /**
   *
   * @param {string!} token - Authorization token
   * @param {1|2|3|4|5|6|7} dayNumber - Day number
   * @param {'current'|'next'} weekString - Week string to which day belongs
   */
  public static getDaySwappableRecipes(
    token: string,
    dayNumber: number,
    weekString: string
  ) {
    return API_Root<
      {
        recipes: IRecipe[];
        swaps_for_meals: { [key in number]: number[] };
      },
      Params
    >({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/recipes/swappable-for-meals/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      params: {
        week: weekString,
        day_number: dayNumber,
      },
    });
  }

  public static getSwappableRecipesByLabel(token: string, weekNumber: number) {
    return API_Root<{ [key in MealLabels]: IRecipe[] }, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/swappable-recipes-by-label/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      params: {
        week_number: weekNumber,
      },
    });
  }

  public static getAllCheckins(token: string) {
    return API_Root<ICheckin[], unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/checkins/`,
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  }

  public static createCheckin(token: string, data: Partial<ICheckin>) {
    return API_Root({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/checkins/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        Authorization: `Token ${token}`,
      },
      data,
    });
  }

  public static getMeal({
    mealId,
    token,
  }: {
    mealId: string;
    token: string | null;
  }) {
    return API_Root<IMeal, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/usermeals/${mealId}/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
    });
  }

  public static storeCheckin(
    token: string,
    metabolicType: number,
    weight: string,
    weekNumber: number
  ) {
    const data: Partial<Payload> = {
      metabolic_type: metabolicType,
      week_number: weekNumber,
      weight: weight,
    };
    return API_Root({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/checkins/`,
      headers: {
        Authorization: `Token ${token}`,
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      data,
    });
  }

  static updateAccountInfo(token: string, userInfo: IUser) {
    const userName = userInfo.username ?? userInfo.first_name;
    if (!userName) {
      throw new Error("Username or first name must be provided");
    }
    return API_Root<IUser, IUser>({
      method: "put",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/accounts/${userName}/`,
      headers: {
        Authorization: `Token ${token}`,
      },
      data: userInfo,
    });
  }

  // this API is used in onboarding and after checkin to replace upcoming week's meal plan with swappable recipes
  static swapToRecipes(
    token: string,
    recipesAndLabels: ChoosingRecipe[],
    weekNumber: number
  ) {
    // If error.response.status === 409, it means this request has been tried when the meal plan generation was in progress
    return API_Root<IRecipe, Partial<Payload>>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/usermeals/swap-to-recipes/`,
      headers: {
        Authorization: `Token ${token}`,
      },
      params: {
        week_number: weekNumber,
      },
      data: {
        recipes_and_labels: recipesAndLabels,
      },
    });
  }

  static logMeal(token: string | null, mealId: number) {
    return API_Root({
      method: "post",
      url: `${this.domain}/api/v2/loggedmeals/`,
      headers: {
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
      data: {
        meal: mealId,
      },
    });
  }

  static unlogMeal(token: string | null, mealId: number, loggedId?: number) {
    return API_Root({
      method: "delete",
      url: `${this.domain}/api/v2/loggedmeals/${loggedId}/?logged_meal__meal_id=${mealId}`,
      headers: {
        Authorization: `Token ${token}`,
      },
    });
  }

  public static swapMeal({
    mealId,
    token,
    meal,
  }: {
    mealId: number;
    token?: string;
    meal: IMeal<IRecipeInLibrary>;
  }) {
    if (!token) {
      throw new Error("User is not authenticated");
    }
    return API_Root<IMeal, unknown>({
      method: "put",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/usermeals/${mealId}/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        ...(token ? { Authorization: `Token ${token}` } : {}),
      },
      data: meal,
    });
  }

  static getRecipesFromTags({
    token,
    tags,
    mealId,
    weekString,
  }: {
    token: string | null;
    tags?: IRecipeFilterTag[];
    mealId?: string | null;
    weekString: string;
  }) {
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const tagsQuery =
      tags && tags.length
        ? tags.reduce((result, tag, index) => {
            return `${result}tags__slug=${tag}${
              index === tags.length - 1 ? "" : "&"
            }`;
          }, "?")
        : "";

    const url = `${FuturHealthLegacyAPI.domain}/api/v2/all-recipes/${tagsQuery}${
      mealId ? `${tagsQuery === "" ? "?" : "&"}user_meal=${mealId}` : ""
    }${weekString === "next" ? "&swappable=next_week" : ""}`;

    return API_Root<IRecipeInLibrary[], unknown>({
      method: "get",
      url,
      headers: { Authorization: `Token ${token}` },
    });
  }

  static getRecipeTagsCategories(token: string | null) {
    if (!token) {
      throw new Error("User is not authenticated");
    }

    return API_Root<IRecipeTagsCategory[], unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v2/tags-categories/`,
      headers: { Authorization: `Token ${token}` },
    });
  }

  static checkIfAccountExist(email: string) {
    return API_Root<ICheckOLAccount, unknown>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/auth/check-ol-account/`,
      headers: {
        "Content-Type": "application/json",
      },
      data: { email },
    });
  }

  public static createAccountCheckin(data: {
    current_weight: number;
    goal_weight: number;
    gender: string;
    birthdate: string;
    age: number;
    first_name: string;
    last_name: string;
    email: string;
    cell_phone?: string | null;
    user_survey_pk?: number;
    metabolic_type?: number;
    height: number;
  }) {
    return API_Root<ICreateAccountCheckinResponse, typeof data>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/auth/create_account_checkin/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      data,
    });
  }

  public static createAccount(data: {
    current_weight: number;
    goal_weight: number;
    gender: string;
    birthdate: string;
    age: number;
    first_name: string;
    last_name: string;
    email: string;
    cell_phone?: string | null;
    user_survey_pk?: number;
    metabolic_type?: number;
    height: number;
  }) {
    return API_Root<ICreateAccountCheckinResponse, typeof data>({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/auth/create_account/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      data,
    });
  }

  public static getUtmParams() {
    return API_Root({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/utm-params/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });
  }

  public static getAffiliate(params: {
    surveysessionid?: string;
    affid?: string[];
    moonsessionid?: string;
    redirect_source?: string;
    posthog_session_id?: string;
    posthog_distinct_id?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params.surveysessionid)
      queryParams.set("surveysessionid", params.surveysessionid);
    if (params.affid) {
      params.affid.forEach((id) => queryParams.append("affid[]", id));
    }
    if (params.moonsessionid)
      queryParams.set("moonsessionid", params.moonsessionid);
    if (params.redirect_source)
      queryParams.set("redirect_source", params.redirect_source);
    if (params.posthog_session_id)
      queryParams.set("posthog_session_id", params.posthog_session_id);
    if (params.posthog_distinct_id)
      queryParams.set("posthog_distinct_id", params.posthog_distinct_id);

    return API_Root({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/affilate/?${queryParams.toString()}`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });
  }

  public static updateUser(
    token: string,
    username: string,
    userData: Partial<IUser>
  ) {
    return API_Root<IUser, Partial<IUser>>({
      method: "put",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/accounts/${username}/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      data: userData,
    });
  }

  public static setIsMicrodosingUser(
    token: string,
    isMicrodosingUser: boolean
  ) {
    return API_Root({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/healthie/set-microdosing/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      data: {
        is_microdosing: isMicrodosingUser,
      },
    });
  }

  public static createCustomer(
    token: string,
    customerData: Record<string, unknown>
  ) {
    return API_Root({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/customers/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      data: customerData,
    });
  }

  public static createOrder(
    token: string,
    customerId: number | string,
    orderData: Record<string, unknown>
  ) {
    return API_Root({
      method: "post",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/customers/${customerId}/orders/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
      data: orderData,
    });
  }

  public static getPlan(token: string, selectedMembership: string) {
    return API_Root({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/plans/${selectedMembership}/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
    });
  }

  /**
   * Retrieves user authentication token and data based on UID
   * @param uid - User unique identifier
   * @returns Promise containing user data and token
   */
  public static getTokenFromUID(uid: string) {
    return API_Root<IAuth & { user: IUser }, unknown>({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/auth/uid/${uid}/`,
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
    });
  }

  /**
   * Retrieves patient data from Healthie including initial appointment info
   * @param token - User authentication token
   * @returns Promise containing patient data with appointment information
   */
  public static getPatient(token: string) {
    return API_Root({
      method: "get",
      url: `${FuturHealthLegacyAPI.domain}/api/v1/healthie/patient/`,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${token}`,
      },
    });
  }
}
